import { css, cx } from '@emotion/css';
import { useState, useRef, type HTMLAttributes } from 'react';
import { CreditCard, QrCode, Wallet, ChevronRight } from 'lucide-react';
import PaymentMethodCreditCard, {
  type CreditCardData,
} from '../payment-method-credit-card/payment-method-credit-card';
import PaymentMethodDebitCard, {
  type DebitCardData,
} from '../payment-method-debit-card/payment-method-debit-card';
import PaymentMethodPix, {
  type PixData,
} from '../payment-method-pix/payment-method-pix';
import PaymentMethodBalance, {
  type BalanceData,
} from '../payment-method-balance/payment-method-balance';
import { useAuth } from '../../../auth/hooks/use-auth';

const container = css({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-md)',
});

const title = css({
  fontSize: 'var(--fs-500)',
  fontWeight: 600,
  color: 'var(--pc-800)',
  marginBottom: 'var(--spacing-lg)',
  position: 'relative',
  display: 'inline-block',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-8px',
    left: 0,
    width: '40px',
    height: '2px',
    backgroundColor: 'var(--pc-400)',
    borderRadius: '2px',
  },
});

const optionWrapper = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: 'var(--spacing-lg)',
  border: '1px solid var(--g-200)',
  borderRadius: 'var(--spacing-md)',
  cursor: 'pointer',
  fontSize: 'var(--fs-400)',
  transition: 'all 0.2s ease',
  backgroundColor: 'var(--primary-color)',
  '&:hover': {
    borderColor: 'var(--pc-600)',
    backgroundColor: 'var(--g-50)',
  },
  '@media (max-width: 768px)': {
    padding: 'var(--spacing-md)',
  },
});

const optionWrapperActive = css({
  borderColor: 'var(--pc-600)',
  backgroundColor: 'var(--g-50)',
});

const option = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-md)',
  '& svg': {
    width: 'var(--fs-600)',
    height: 'var(--fs-600)',
    color: 'var(--g-600)',
  },
});

const optionArrow = css({
  width: 'var(--fs-500)',
  height: 'var(--fs-500)',
  color: 'var(--g-400)',
  transition: 'transform 0.2s ease, color 0.2s ease',
});

const optionArrowActive = css({
  transform: 'rotate(-90deg)',
  color: 'var(--pc-600)',
});

const accordion = css({
  backgroundColor: 'white',
  border: '1px solid var(--g-200)',
  borderRadius: 'var(--spacing-md)',
  marginTop: 'calc(-1 * var(--spacing-sm))',
  marginBottom: 'var(--spacing-md)',
  overflow: 'hidden',
  padding: 'var(--spacing-lg)',
  animation: 'slideDown 0.3s ease-out',
  '@keyframes slideDown': {
    from: {
      opacity: 0,
      transform: 'translateY(-10px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  '@media (max-width: 768px)': {
    padding: 'var(--spacing-md)',
  },
});

const legalInfo = css({
  width: '100%',
  marginTop: 'var(--spacing-2xl)',
  paddingTop: 'var(--spacing-lg)',
  borderTop: '1px solid var(--g-200)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-lg)',
});

const support = css({
  marginBottom: 'var(--spacing-md)',
  '& p': {
    margin: 'var(--spacing-xs) 0',
    fontSize: 'var(--fs-300)',
    color: 'var(--g-700)',
  },
});

const link = css({
  color: 'var(--pc-600)',
  textDecoration: 'none',
  fontWeight: 500,
  transition: 'all 0.2s ease',
  '&:hover': {
    color: 'var(--pc-700)',
    textDecoration: 'underline',
  },
});

const terms = css({
  backgroundColor: 'rgba(var(--pcrgba-100), 0.2)',
  padding: 'var(--spacing-md)',
  borderRadius: 'var(--spacing-sm)',
  border: '1px solid rgba(var(--pcrgba-200), 0.5)',
  '@media (max-width: 768px)': {
    padding: 'var(--spacing-sm)',
  },
});

const termsTitle = css({
  fontWeight: 600,
  marginBottom: 'var(--spacing-sm)',
  fontSize: 'var(--fs-400)',
  color: 'var(--pc-700)',
});

const termsList = css({
  listStyleType: 'none',
  paddingLeft: 0,
  '& li': {
    position: 'relative',
    paddingLeft: '24px',
    marginBottom: 'var(--spacing-xs)',
    fontSize: 'var(--fs-300)',
    lineHeight: 1.5,
    color: 'var(--g-700)',
    '&::before': {
      content: '"✓"',
      position: 'absolute',
      left: 0,
      color: 'var(--pc-500)',
      fontWeight: 'bold',
    },
  },
});

const footer = css({
  textAlign: 'center',
  marginTop: 'var(--spacing-md)',
  paddingTop: 'var(--spacing-md)',
  borderTop: '1px solid var(--g-200)',
  '& p': {
    fontSize: 'var(--fs-300)',
    color: 'var(--g-500)',
  },
});

type PaymentMethod = 'credit' | 'debit' | 'pix' | 'balance' | null;
type PaymentData = CreditCardData | DebitCardData | PixData | BalanceData | null;

export interface PaymentCheckoutProps extends HTMLAttributes<HTMLDivElement> {
  totalValue?: number;
  document?: string; // CPF from session or profile
  onValidationChange?: (isValid: boolean) => void;
  onPaymentDataChange?: (data: PaymentData, method: PaymentMethod) => void;
}

export function PaymentCheckout({
  totalValue = 0,
  document,
  onValidationChange,
  onPaymentDataChange,
  className,
  style,
  ...props
}: PaymentCheckoutProps) {
  const { isAuthenticated } = useAuth();
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>(null);
  // Local mirrors removed; parent holds canonical state via callbacks

  // Refs for each payment method section
  const creditRef = useRef<HTMLDivElement>(null);
  const debitRef = useRef<HTMLDivElement>(null);
  const pixRef = useRef<HTMLDivElement>(null);
  const balanceRef = useRef<HTMLDivElement>(null);

  const handleMethodSelect = (method: PaymentMethod) => {
    if (selectedMethod === method) {
      // Don't deselect, just keep it selected
      return;
    }
    setSelectedMethod(method);
    onValidationChange?.(false);
    onPaymentDataChange?.(null, method);

    // Scroll to the payment method form after a short delay to allow accordion to open
    setTimeout(() => {
      let targetRef = null;

      switch (method) {
        case 'credit':
          targetRef = creditRef.current;
          break;
        case 'debit':
          targetRef = debitRef.current;
          break;
        case 'pix':
          targetRef = pixRef.current;
          break;
        case 'balance':
          targetRef = balanceRef.current;
          break;
      }

      if (targetRef) {
        targetRef.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }, 100);
  };

  const handleDataChange = (data: PaymentData, valid: boolean) => {
    onValidationChange?.(valid);
    onPaymentDataChange?.(data, selectedMethod);
  };

  return (
    <div className={cx(container, className)} style={style} {...props}>
      <h3 className={title}>Método de Pagamento</h3>

      {/* Credit Card */}
      <div
        className={cx(optionWrapper, {
          [optionWrapperActive]: selectedMethod === 'credit',
        })}
        onClick={() => handleMethodSelect('credit')}
      >
        <div className={option}>
          <CreditCard />
          <span>Cartão de crédito</span>
        </div>
        <ChevronRight
          className={cx(optionArrow, {
            [optionArrowActive]: selectedMethod === 'credit',
          })}
        />
      </div>

      {selectedMethod === 'credit' && (
        <div className={accordion} ref={creditRef}>
          <PaymentMethodCreditCard
            document={document}
            totalValue={totalValue}
            onDataChange={(data, valid) =>
              handleDataChange(data as PaymentData, valid)
            }
          />
        </div>
      )}

      {/* Debit Card */}
      <div
        className={cx(optionWrapper, {
          [optionWrapperActive]: selectedMethod === 'debit',
        })}
        onClick={() => handleMethodSelect('debit')}
      >
        <div className={option}>
          <CreditCard />
          <span>Cartão de débito</span>
        </div>
        <ChevronRight
          className={cx(optionArrow, {
            [optionArrowActive]: selectedMethod === 'debit',
          })}
        />
      </div>

      {selectedMethod === 'debit' && (
        <div className={accordion} ref={debitRef}>
          <PaymentMethodDebitCard
            onDataChange={(data, valid) =>
              handleDataChange(data as PaymentData, valid)
            }
          />
        </div>
      )}

      {/* PIX */}
      <div
        className={cx(optionWrapper, {
          [optionWrapperActive]: selectedMethod === 'pix',
        })}
        onClick={() => handleMethodSelect('pix')}
      >
        <div className={option}>
          <QrCode />
          <span>PIX</span>
        </div>
        <ChevronRight
          className={cx(optionArrow, {
            [optionArrowActive]: selectedMethod === 'pix',
          })}
        />
      </div>

      {selectedMethod === 'pix' && (
        <div className={accordion} ref={pixRef}>
          <PaymentMethodPix
            onDataChange={(data, valid) =>
              handleDataChange(data as PaymentData, valid)
            }
          />
        </div>
      )}

      {/* Balance - only show if user is authenticated */}
      {isAuthenticated && (
        <>
          <div
            className={cx(optionWrapper, {
              [optionWrapperActive]: selectedMethod === 'balance',
            })}
            onClick={() => handleMethodSelect('balance')}
          >
            <div className={option}>
              <Wallet />
              <span>Saldo</span>
            </div>
            <ChevronRight
              className={cx(optionArrow, {
                [optionArrowActive]: selectedMethod === 'balance',
              })}
            />
          </div>

          {selectedMethod === 'balance' && (
            <div className={accordion} ref={balanceRef}>
              <PaymentMethodBalance
                totalValue={totalValue}
                onDataChange={(data, valid) =>
                  handleDataChange(data as PaymentData, valid)
                }
              />
            </div>
          )}
        </>
      )}

      {/* Legal Information */}
      <div className={legalInfo}>
        <div className={support}>
          <p>
            Dúvidas sobre o evento?{' '}
            <a href="mailto:ajuda@anchoringressos.com.br" className={link}>
              ajuda@anchoringressos.com.br
            </a>
          </p>
          <p>
            Problemas com a compra? Acesse nossa{' '}
            <a
              href="https://ajuda.anchoringressos.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className={link}
            >
              Central de Ajuda
            </a>
          </p>
        </div>

        <div className={terms}>
          <h4 className={termsTitle}>Ao clicar em finalizar, declaro que:</h4>
          <ul className={termsList}>
            <li>Estou ciente dos dados do evento, incluindo local, data e horário</li>
            <li>
              Concordo com os{' '}
              <a href="/politicas/termos" target="_blank" rel="noopener noreferrer" className={link}>
                Termos e Condições
              </a>{' '}
              e a{' '}
              <a href="/politicas/privacidade" target="_blank" rel="noopener noreferrer" className={link}>
                Política de Privacidade
              </a>
            </li>
            <li>
              Concordo com a{' '}
              <a href="/politicas/meia-entrada" target="_blank" rel="noopener noreferrer" className={link}>
                Política de Meia-Entrada
              </a>{' '}
              e a{' '}
              <a href="/politicas/reembolso" target="_blank" rel="noopener noreferrer" className={link}>
                Política de Reembolso
              </a>
            </li>
            <li>Confirmo que as informações fornecidas são verdadeiras e corretas</li>
            <li>Entendo que os ingressos são pessoais e intransferíveis</li>
          </ul>
        </div>

        <div className={footer}>
          <p>Anchor Ingressos © 2026 - Todos os direitos reservados.</p>
        </div>
      </div>
    </div>
  );
}

export default PaymentCheckout;
